import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Globe, Plus, Edit2, Trash2, ExternalLink, MapPin } from 'lucide-react';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface Website {
  id: number;
  name: string;
  url: string;
  type: string;
  description?: string;
  platform?: string;
  sitemap_url?: string;
  is_primary: boolean;
}

interface Props {
  clientId: number;
}

export default function WebsitesTab({ clientId }: Props) {
  const [websites, setWebsites] = useState<Website[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingWebsite, setEditingWebsite] = useState<Website | null>(null);
  const [websiteForm, setWebsiteForm] = useState({
    name: '',
    url: '',
    type: 'website',
    description: '',
    platform: '',
    sitemap_url: '',
    is_primary: false
  });

  useEffect(() => {
    loadWebsites();
  }, [clientId]);

  const loadWebsites = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/websites?client_id=${clientId}`);
      setWebsites(response.data.websites || []);
    } catch (error) {
      console.error('Failed to load websites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (website: Website) => {
    setEditingWebsite(website);
    setWebsiteForm({
      name: website.name,
      url: website.url,
      type: website.type,
      description: website.description || '',
      platform: website.platform || '',
      sitemap_url: website.sitemap_url || '',
      is_primary: website.is_primary
    });
    setShowModal(true);
  };

  const handleDelete = async (websiteId: number) => {
    if (!confirm('Are you sure you want to delete this website?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/websites/${websiteId}`);
      await loadWebsites();
    } catch (error) {
      console.error('Failed to delete website:', error);
      alert('Failed to delete website');
    }
  };

  const handleSave = async () => {
    try {
      if (editingWebsite) {
        // Update existing
        await axios.put(`${API_BASE_URL}/websites/${editingWebsite.id}`, websiteForm);
      } else {
        // Create new
        await axios.post(`${API_BASE_URL}/websites`, {
          ...websiteForm,
          client_id: clientId
        });
      }
      setShowModal(false);
      setEditingWebsite(null);
      setWebsiteForm({
        name: '',
        url: '',
        type: 'website',
        description: '',
        platform: '',
        sitemap_url: '',
        is_primary: false
      });
      await loadWebsites();
    } catch (error) {
      console.error('Failed to save website:', error);
      alert('Failed to save website');
    }
  };

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading websites...</div>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Websites & Properties</h2>
          <p className="text-sm text-gray-600 mt-1">Manage client websites and online properties</p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Add Website
        </button>
      </div>

      {websites.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border border-gray-200">
          <Globe className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600">No websites added yet</p>
          <button
            onClick={() => setShowModal(true)}
            className="mt-4 px-4 py-2 text-sm font-medium text-blue-600 hover:underline"
          >
            Add your first website
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Website Name</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">URL</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Platform</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sitemap</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {websites.map((website) => (
                <tr key={website.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="font-medium text-gray-900 text-sm">{website.name}</div>
                    {website.description && (
                      <div className="text-xs text-gray-500 mt-0.5">{website.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600 capitalize">{website.type}</span>
                  </td>
                  <td className="px-6 py-4">
                    <a
                      href={website.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                    >
                      <Globe className="w-4 h-4" />
                      {website.url}
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {website.platform ? (
                      <span className="text-sm text-gray-600">{website.platform}</span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {website.sitemap_url ? (
                      <a
                        href={website.sitemap_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:underline"
                      >
                        <MapPin className="w-4 h-4" />
                        View
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {website.is_primary && (
                      <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded">
                        Primary
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(website)}
                      className="text-blue-600 hover:text-blue-700 mr-3"
                      title="Edit"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(website.id)}
                      className="text-red-600 hover:text-red-700"
                      title="Delete"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Website Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">
              {editingWebsite ? 'Edit Website' : 'Add Website'}
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                <input
                  type="text"
                  value={websiteForm.name}
                  onChange={(e) => setWebsiteForm({ ...websiteForm, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Main Website"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">URL *</label>
                <input
                  type="url"
                  value={websiteForm.url}
                  onChange={(e) => setWebsiteForm({ ...websiteForm, url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={websiteForm.type}
                  onChange={(e) => setWebsiteForm({ ...websiteForm, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="website">Website</option>
                  <option value="landing_page">Landing Page</option>
                  <option value="blog">Blog</option>
                  <option value="ecommerce">E-commerce</option>
                  <option value="portfolio">Portfolio</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                <input
                  type="text"
                  value={websiteForm.platform}
                  onChange={(e) => setWebsiteForm({ ...websiteForm, platform: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="WordPress, Shopify, etc."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sitemap URL</label>
                <input
                  type="url"
                  value={websiteForm.sitemap_url}
                  onChange={(e) => setWebsiteForm({ ...websiteForm, sitemap_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="https://example.com/sitemap.xml"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={websiteForm.description}
                  onChange={(e) => setWebsiteForm({ ...websiteForm, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_primary"
                  checked={websiteForm.is_primary}
                  onChange={(e) => setWebsiteForm({ ...websiteForm, is_primary: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="is_primary" className="ml-2 text-sm text-gray-700">
                  Set as primary website
                </label>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingWebsite(null);
                  setWebsiteForm({
                    name: '',
                    url: '',
                    type: 'website',
                    description: '',
                    platform: '',
                    sitemap_url: '',
                    is_primary: false
                  });
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                {editingWebsite ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}